// Formatting helpers that were copy-pasted into ~20 modules.

import type { ISODate, Numeric } from '../types/common';
import { getPreferences } from './preferences';

// Amounts follow the currency chosen in Settings. Formatters are built once per
// code; INR keeps the Indian lakh/crore grouping, anything else groups in
// thousands. An unknown code must not crash a report, so it falls back to INR.
const formatters = new Map<string, Intl.NumberFormat>();

const currencyFormatter = (precise: boolean): Intl.NumberFormat => {
  const code = getPreferences().currencyCode;
  const key = `${code}:${precise ? 2 : 0}`;
  let formatter = formatters.get(key);
  if (!formatter) {
    const digits = precise ? 2 : 0;
    try {
      formatter = new Intl.NumberFormat(code === 'INR' ? 'en-IN' : 'en-US', {
        style: 'currency', currency: code, minimumFractionDigits: digits, maximumFractionDigits: digits
      });
    } catch {
      formatter = new Intl.NumberFormat('en-IN', {
        style: 'currency', currency: 'INR', minimumFractionDigits: digits, maximumFractionDigits: digits
      });
    }
    formatters.set(key, formatter);
  }
  return formatter;
};

/** The bare symbol of the chosen currency — "₹", "$", "£" — for chart axes. */
export const currencySymbol = (): string => {
  const parts = currencyFormatter(false).formatToParts(0);
  return parts.find((p) => p.type === 'currency')?.value || getPreferences().currencyCode;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-07-28" -> "28/07/2026", or whichever layout Settings chose. */
export const formatDate = (dateString?: ISODate | null, fallback = '-'): string => {
  if (!dateString) return fallback;
  const parts = String(dateString).split('T')[0].split('-');
  if (parts.length !== 3) return String(dateString);
  const [y, m, d] = parts;
  switch (getPreferences().dateFormat) {
    case 'DD-MM-YYYY': return `${d}-${m}-${y}`;
    case 'MM/DD/YYYY': return `${m}/${d}/${y}`;
    case 'YYYY-MM-DD': return `${y}-${m}-${d}`;
    case 'DD MMM YYYY': {
      const month = MONTHS[Number(m) - 1];
      return month ? `${d} ${month} ${y}` : `${d}/${m}/${y}`;
    }
    default: return `${d}/${m}/${y}`;
  }
};

/** A clock time, however it was written. */
export interface ClockTime {
  hour24: number;
  minute: number;
}

/**
 * Reads a time in any of the forms the app has stored or a user might type:
 * "15:31", "15:31:47" (24-hour `type="time"`), "03:31 PM", "3:31pm", "8 am".
 * Null for anything else — a value that is not understood must never be
 * silently turned into a different time. "06:30 PM" used to be read as
 * six-thirty in the morning because only the digits were looked at (CHM-012).
 */
export const parseTime = (value?: string | null): ClockTime | null => {
  const raw = String(value ?? '').trim().toUpperCase();
  const m = raw.match(/^(\d{1,2})(?::(\d{2}))?(?::\d{2})?\s*(AM|PM|A\.M\.|P\.M\.)?$/);
  if (!m) return null;
  let hour = parseInt(m[1], 10);
  const minute = m[2] ? parseInt(m[2], 10) : 0;
  const period = m[3] ? m[3][0] : '';
  if (!Number.isFinite(hour) || minute > 59) return null;
  if (period) {
    if (hour < 1 || hour > 12) return null;
    hour = (hour % 12) + (period === 'P' ? 12 : 0);
  } else if (!m[2] || hour > 23) {
    // A bare number with no minutes and no AM/PM is too ambiguous to accept.
    return null;
  }
  return { hour24: hour, minute };
};

/** "03:31 PM" — the form the church reads and the attendance register stores. */
export const to12h = (t: ClockTime): string => {
  const h = t.hour24 % 12 || 12;
  return `${String(h).padStart(2, '0')}:${String(t.minute).padStart(2, '0')} ${t.hour24 >= 12 ? 'PM' : 'AM'}`;
};

/** "15:31" — what a native `type="time"` input wants. */
export const to24h = (t: ClockTime): string =>
  `${String(t.hour24).padStart(2, '0')}:${String(t.minute).padStart(2, '0')}`;

/**
 * Any stored time shown 12-hour: "15:31" -> "03:31 PM", "06:30 PM" stays
 * "06:30 PM". Anything that is not a time is returned unchanged rather than
 * guessed at.
 */
export const formatTime = (value?: string | null, fallback = '-'): string => {
  const raw = String(value ?? '').trim();
  if (!raw) return fallback;
  const t = parseTime(raw);
  return t ? to12h(t) : raw;
};

/** "2026-07-28" -> "28 Jul 2026" */
export const formatDateLong = (dateString?: ISODate | null, fallback = '-'): string => {
  if (!dateString) return fallback;
  const d = new Date(String(dateString).split('T')[0]);
  if (isNaN(d.getTime())) return String(dateString);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const formatDateTime = (
  dateString?: ISODate | null,
  time?: string | null,
  fallback = '-'
): string => {
  if (!dateString) return fallback;
  return time ? `${formatDate(dateString)} at ${time}` : formatDate(dateString);
};

export const formatCurrency = (amount?: Numeric | null, fallback = '-'): string => {
  if (amount === null || amount === undefined || amount === '') return fallback;
  const n = parseFloat(String(amount));
  return isNaN(n) ? fallback : currencyFormatter(false).format(n);
};

export const formatCurrencyPrecise = (amount?: Numeric | null, fallback = '-'): string => {
  if (amount === null || amount === undefined || amount === '') return fallback;
  const n = parseFloat(String(amount));
  return isNaN(n) ? fallback : currencyFormatter(true).format(n);
};

export const formatNumber = (value?: Numeric | null, fallback = '0'): string => {
  const n = Number(value);
  return isNaN(n) ? fallback : n.toLocaleString('en-IN');
};

/** Today as "YYYY-MM-DD" in local time (toISOString() would shift the date). */
export const todayISO = (): ISODate => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Current month as "YYYY-MM". */
export const currentMonthISO = (): string => todayISO().slice(0, 7);

export interface AnniversaryInfo {
  /** Days until the next occurrence. 0 means today. */
  days: number;
  /** Years completed at that next occurrence. */
  years: number;
}

/** Days until the next anniversary of `dateString`. Null if unparseable. */
export const daysUntilAnniversary = (dateString?: ISODate | null): AnniversaryInfo | null => {
  if (!dateString || String(dateString).length < 10) return null;
  const [y, m, d] = String(dateString).split('-').map(Number);
  const source = new Date(y, m - 1, d);
  if (isNaN(source.getTime())) return null;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const next = new Date(today.getFullYear(), source.getMonth(), source.getDate());
  if (next < today) next.setFullYear(today.getFullYear() + 1);

  return {
    days: Math.ceil((next.getTime() - today.getTime()) / 86400000),
    years: next.getFullYear() - source.getFullYear()
  };
};

/** Completed years since a date of birth. */
export const ageFrom = (dateString?: ISODate | null, fallback: string | number = '-'): string | number => {
  if (!dateString) return fallback;
  const dob = new Date(dateString);
  if (isNaN(dob.getTime())) return fallback;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) age -= 1;
  return age < 0 ? fallback : age;
};

export const ordinal = (n: Numeric): string => {
  const num = Number(n);
  if (isNaN(num)) return String(n);
  const suffix = num === 1 ? 'st' : num === 2 ? 'nd' : num === 3 ? 'rd' : 'th';
  return `${num}${suffix}`;
};

export const relativeDayLabel = (days: number): string =>
  days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `In ${days} Days`;

export const titleCase = (s?: string | null): string =>
  String(s || '').replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

export const truncate = (s?: string | null, max = 60): string => {
  const str = String(s || '');
  return str.length > max ? `${str.slice(0, max - 1)}…` : str;
};
